import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const KEY = "lesalon:cookies-acknowledged";
const AUTO_DISMISS_MS = 14000; // gently fade after ~14s so it never lingers

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const dismissTimer = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (typeof window !== "undefined" && !window.localStorage?.getItem(KEY)) {
        setVisible(true);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;
    dismissTimer.current = setTimeout(() => {
      // auto-acknowledge after a polite delay so the banner never blocks forms
      try { window.localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
      setVisible(false);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(dismissTimer.current);
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    try { window.localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      className="cookie-banner-root"
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        maxWidth: 300,
        background: "var(--surface)",
        border: "1px solid var(--brand-accent)",
        padding: "12px 14px",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        boxShadow: "0 10px 28px rgba(51,51,51,0.14)",
        zIndex: 200,
        animation: "salonCookieSlide 0.35s ease-out",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 13, lineHeight: 1.4, margin: 0, color: "var(--text-primary)" }}>
          First-party cookies only — no tracking, no ads.{" "}
          <Link to="/privacy" style={{ color: "var(--brand-accent)", borderBottom: "1px solid var(--brand-accent)", textDecoration: "none", whiteSpace: "nowrap" }}>
            Privacy
          </Link>
        </p>
        <button
          type="button"
          onClick={dismiss}
          data-testid="cookie-dismiss"
          style={{
            marginTop: 8,
            background: "var(--brand-accent)",
            color: "#FAF9F5",
            border: "none",
            padding: "5px 12px",
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Understood
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Close cookie banner"
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--text-muted)",
          padding: 2,
          marginTop: -2,
          marginRight: -2,
        }}
      >
        <X size={13} />
      </button>
      {/* On mobile, lift above the bottom tab bar so it never covers navigation
          or compose controls. Kept small + corner-anchored so form fields in
          the center of the screen are never blocked. */}
      <style>{`
        @keyframes salonCookieSlide {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        @media (max-width: 767px) {
          .cookie-banner-root {
            bottom: calc(88px + env(safe-area-inset-bottom, 0px)) !important;
            right: 12px !important;
            max-width: calc(100% - 24px) !important;
            width: 280px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CookieBanner;
